import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Clock, Gauge } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, Field, Loading } from "@/components/common";
import { api, errMsg } from "@/lib/api";
import { money } from "@/lib/format";

const OPTS = {
  cleaning_type: [["standard", "Standard"], ["deep", "Deep"], ["move", "Move-in/out"]],
  condition: [["light", "Light"], ["average", "Average"], ["heavy", "Heavy"]],
  frequency: [["one_time", "One-time"], ["weekly", "Weekly"], ["biweekly", "Every 2 weeks"], ["monthly", "Monthly"]],
};
const LABELS = { cleaning_type: "Cleaning type", condition: "Home condition", frequency: "Frequency" };

function Breakdown({ r }) {
  return (
    <div data-testid="calc-breakdown">
      <div className="space-y-2 text-sm">
        {r.lines.map((l, i) => (
          <div key={i} className={`flex justify-between gap-4 ${l.kind === "note" ? "text-xs text-slate-500" : ""}`} data-testid={`calc-line-${i}`}>
            <span>{l.label}</span>{l.kind !== "note" && <span className={l.amount < 0 ? "text-teal-700" : "text-slate-900"}>{l.amount < 0 ? `−${money(-l.amount)}` : money(l.amount)}</span>}
          </div>
        ))}
      </div>
      <div className="flex justify-between items-baseline border-t border-slate-200 mt-4 pt-4">
        <span className="font-medium text-slate-900">Total per visit</span>
        <span className="font-display text-3xl font-semibold text-slate-900" data-testid="calc-total">{money(r.total)}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="h-3.5 w-3.5" />Labor estimate</div><div className="font-semibold" data-testid="calc-hours">{r.labor_hours} hrs</div></div>
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500 flex items-center gap-1"><Gauge className="h-3.5 w-3.5" />Effective rate</div><div className="font-semibold" data-testid="calc-rate">{r.effective_hourly ? `${money(r.effective_hourly)}/hr` : "—"}</div></div>
      </div>
    </div>
  );
}

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [extras, setExtras] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [inp, setInp] = useState({ sqft: 1500, bedrooms: 3, bathrooms: 2, cleaning_type: "standard", condition: "average", frequency: "one_time", extras: [] });
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/business"), api.get("/clients")]).then(([b, c]) => { setExtras(b.data.pricing.extras); setClients(c.data); }).catch((e) => setError(errMsg(e)));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/pricing/calculate", { ...inp, sqft: Number(inp.sqft), bedrooms: Number(inp.bedrooms), bathrooms: Number(inp.bathrooms) })
        .then((r) => { setRes(r.data); setError(""); }).catch((e) => setError(errMsg(e)));
    }, 300);
    return () => clearTimeout(t);
  }, [inp]);

  const set = (k, v) => setInp((x) => ({ ...x, [k]: v }));
  const toggleExtra = (key, on) => set("extras", on ? [...inp.extras, key] : inp.extras.filter((x) => x !== key));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pricing/estimate", { ...inp, sqft: Number(inp.sqft), bedrooms: Number(inp.bedrooms), bathrooms: Number(inp.bathrooms), client_id: clientId });
      toast.success(`Estimate ${data.number} created`);
      navigate(`/estimates/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!extras) return error ? <p className="text-red-700">{error}</p> : <Loading />;

  return (
    <div data-testid="calculator-page">
      <PageHeader title="Price calculator" subtitle="Uses your pricing rules from Settings → Pricing." />
      <div className="grid lg:grid-cols-5 gap-4 md:gap-6">
        <Panel className="lg:col-span-3" testId="calc-inputs">
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Home size (sq ft)" htmlFor="sqft"><Input id="sqft" type="number" min={100} value={inp.sqft} onChange={(e) => set("sqft", e.target.value)} className="h-11" data-testid="calc-sqft-input" /></Field>
            <Field label="Bedrooms" htmlFor="beds"><Input id="beds" type="number" min={0} value={inp.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} className="h-11" data-testid="calc-bedrooms-input" /></Field>
            <Field label="Bathrooms" htmlFor="baths"><Input id="baths" type="number" min={0} step="0.5" value={inp.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} className="h-11" data-testid="calc-bathrooms-input" /></Field>
            {Object.entries(OPTS).map(([k, opts]) => (
              <Field key={k} label={LABELS[k]}>
                <Select value={inp[k]} onValueChange={(v) => set(k, v)}>
                  <SelectTrigger className="h-11" data-testid={`calc-${k}-select`}><SelectValue /></SelectTrigger>
                  <SelectContent>{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            ))}
          </div>
          <div className="mt-6">
            <div className="text-sm font-medium text-slate-700 mb-2">Optional extras</div>
            <div className="grid sm:grid-cols-2 gap-1">
              {extras.map((x) => (
                <label key={x.key} className="flex items-center gap-3 min-h-[44px] px-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <Checkbox checked={inp.extras.includes(x.key)} onCheckedChange={(v) => toggleExtra(x.key, !!v)} data-testid={`calc-extra-${x.key}`} />
                  <span className="text-sm flex-1">{x.name}</span><span className="text-sm text-slate-500">{money(x.price)}</span>
                </label>
              ))}
            </div>
          </div>
        </Panel>
        <div className="lg:col-span-2 space-y-4">
          <Panel title="Price breakdown" testId="calc-result-panel">
            {error && <p className="text-sm text-red-700 mb-2" data-testid="calc-error">{error}</p>}
            {res ? <Breakdown r={res} /> : <Loading label="Calculating" />}
          </Panel>
          <Panel title="Save as estimate">
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="h-11 mb-3" data-testid="calc-client-select"><SelectValue placeholder="Choose client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="w-full h-11" disabled={!clientId || busy || !res} onClick={save} data-testid="calc-save-estimate-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Create draft estimate</Button>
          </Panel>
        </div>
      </div>
    </div>
  );
}
